import { useRef, useState } from 'react';
import { Badge, Button, useToast } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { useCompanies } from '../../api/hooks';
import type { Company } from '../../api/schemas';
import { buildCompanyVCard, parseVCard, shareVCard, type ParsedVCard } from '../../lib/vcard';
import { NewCompanyModal } from './NewCompanyModal';

function Rating({ value, count }: { value: number | null | undefined; count: number }) {
  if (!value || count === 0) {
    return <span style={{ color: 'var(--sd-text-dimmer)', fontSize: 12.5 }}>—</span>;
  }
  return (
    <span style={{ color: 'var(--sd-yellow-text)', fontFamily: 'var(--sd-font-mono)', fontSize: 12.5 }}>
      {'★'.repeat(Math.round(value))}
      <span style={{ color: 'var(--sd-text-dimmer)', marginLeft: 6 }}>{value.toFixed(1)}</span>
    </span>
  );
}

export function SubdodavatelePage() {
  const { data: companies, isLoading, error } = useCompanies();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);
  const [sharingId, setSharingId] = useState<number | null>(null);
  // Import vizitky z hlavičky: soubor se načte hned a modal se otevře předvyplněný.
  const [importedVCard, setImportedVCard] = useState<ParsedVCard | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // ať jde vybrat stejný soubor znovu
    if (!file) return;
    try {
      const parsed = parseVCard(await file.text());
      if (!parsed) {
        toast.error('Soubor nevypadá jako platná vCard (.vcf).');
        return;
      }
      setImportedVCard(parsed);
      setModalOpen(true);
    } catch {
      toast.error('Soubor se nepodařilo načíst.');
    }
  }

  function closeNewModal() {
    setModalOpen(false);
    setImportedVCard(null);
  }

  async function handleShare(c: Company) {
    const vcard = buildCompanyVCard(c);
    if (!vcard) {
      toast.error('U této firmy chybí jméno i kontakt — není co sdílet.');
      return;
    }
    const filename = `${(c.nazev || c.kontakt_jmeno || 'kontakt').replace(/[^\p{L}\p{N}]+/gu, '_')}.vcf`;
    setSharingId(c.id);
    try {
      const result = await shareVCard(filename, vcard, {
        title: c.nazev || c.kontakt_jmeno || 'Kontakt',
        text: `Kontakt: ${c.nazev || c.kontakt_jmeno}`,
      });
      if (result === 'downloaded') {
        toast.success('Kontakt stažen jako vCard (.vcf) — otevři ho v telefonu nebo přilož do zprávy.');
      } else if (result === 'shared') {
        toast.success('Kontakt odeslán ke sdílení.');
      }
    } catch {
      toast.error('Sdílení se nepodařilo.');
    } finally {
      setSharingId(null);
    }
  }

  const columns: ListColumn<Company>[] = [
    { key: 'nazev', label: 'Firma', primary: true, width: 'minmax(220px, 2.2fr)' },
    { key: 'ico', label: 'IČO', width: '110px', mono: true, dim: true, render: (c) => c.ico || '—' },
    { key: 'obor', label: 'Obor / role', render: (c) => c.obor || c.role || '—' },
    { key: 'kontakt_jmeno', label: 'Kontakt', render: (c) => c.kontakt_jmeno || c.stavbyvedouci || '—' },
    { key: 'kontakt_tel', label: 'Telefon', width: '150px', mono: true, dim: true, render: (c) => c.kontakt_tel || '—' },
    { key: 'rating', label: 'Hodnocení', width: '90px', render: (c) => <Rating value={c.rating_avg} count={c.rating_count} /> },
    {
      key: 'health',
      label: 'Stav',
      width: '140px',
      render: (c) => {
        const ok = c.insurance_ok && c.certificates_ok && c.bozp_ok;
        return ok ? (
          <Badge tone="success" pill dot>OK</Badge>
        ) : (
          <Badge tone="yellow" pill dot>Kontrola potřeba</Badge>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '92px',
      align: 'right',
      render: (c) => (
        <div style={{ display: 'inline-flex', gap: 2 }}>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Upravit firmu ${c.nazev}`}
            title="Upravit / smazat"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(c);
            }}
          >
            ✎
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Sdílet kontakt ${c.nazev}`}
            title="Sdílet kontakt (vCard)"
            disabled={sharingId === c.id}
            onClick={(e) => {
              e.stopPropagation();
              void handleShare(c);
            }}
          >
            ⤴
          </Button>
        </div>
      ),
    },
  ];

  const rows = companies ?? [];

  return (
    <ListPage
      breadcrumb={['Obchod', 'Kontakty']}
      title="Kontakty"
      subtitle={
        isLoading ? 'Načítám…' : error ? 'Chyba při načítání' : `${rows.length} firem v adresáři`
      }
      status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
      statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${rows.length} firem`}
      actions={
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".vcf,text/vcard,text/x-vcard"
            hidden
            onChange={handleImportFile}
          />
          <Button
            variant="secondary"
            leading="📇"
            title="Načíst firmu z vizitky .vcf (vCard) — např. kontakt exportovaný z telefonu"
            onClick={() => fileInputRef.current?.click()}
          >
            Import z vCard
          </Button>
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            + Přidat firmu (ARES)
          </Button>
        </>
      }
      searchPlaceholder="Hledat firmu, IČO, obor…"
    >
      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>Načítám…</div>
      ) : error ? (
        <div style={{
          padding: 24, borderRadius: 4,
          background: 'var(--sd-danger-soft)',
          border: '1px solid rgba(248, 113, 113, 0.4)',
          color: 'var(--sd-danger)', fontSize: 14,
        }}>
          Chyba při načítání: {error instanceof Error ? error.message : 'neznámá'}
        </div>
      ) : (
        <ListTable
          fluid
          columns={columns}
          rows={rows}
          rowKey={(c) => c.id}
          onRowClick={(c) => setEditing(c)}
          emptyTitle="Zatím žádné firmy v adresáři"
          emptyText="Přidejte subdodavatele, investora nebo projektanta."
        />
      )}
      <NewCompanyModal open={modalOpen} onClose={closeNewModal} initialVCard={importedVCard} />
      <NewCompanyModal open={!!editing} company={editing} onClose={() => setEditing(null)} />
    </ListPage>
  );
}
